import {
	type CommandConstructor,
	type CommandProperties,
	type SerializableValue,
} from '@atlassian/rovo-content-bridge-api/command';
import { createCommand } from '@atlassian/rovo-content-bridge-api/command-creator';

/**
 * Values mirror Confluence's AgentContextService and form a cross-application wire contract.
 * Never remove or rename an existing value because independently deployed clients may still send it.
 */
export type AgentType = 'twg' | 'mcp' | 'convo-ai' | 'rovo_chat' | 'unknown';

/** CWR edit surfaces carried on the page-update command wire contract. */
export type CwrRemixInvokedFrom = 'chat_edit' | 'cwr_edit';

/** CWR analytics entry points, including whole-page Create. */
export type CwrRemixEntryPoint = CwrRemixInvokedFrom | 'cwr' | 'chat_view';

export const isCwrRemixInvokedFrom = (value: unknown): value is CwrRemixInvokedFrom =>
	value === 'chat_edit' || value === 'cwr_edit';

export const isCwrRemixEntryPoint = (value: unknown): value is CwrRemixEntryPoint =>
	value === 'cwr' || value === 'chat_view' || isCwrRemixInvokedFrom(value);

export const isCwrRemixResult = ({
	entryPoint,
	aiInvocationSource,
}: {
	entryPoint?: unknown;
	aiInvocationSource?: unknown;
}): boolean => isCwrRemixEntryPoint(entryPoint) || aiInvocationSource === 'cwr';

/**
 * Data payload for updating a Confluence page.
 */
export type ConfluenceUpdatePageUsingToolCallsData = {
	/**
	 * The tools and modifications to be applied to the page.
	 */
	toolCalls: string;
	/**
	 * The content id of the page to be updated.
	 */
	contentId: string;
};

export type ConfluenceUpdatePageCommonData = {
	/**
	 * Tells if the Dynamic UI was generated from a Remix request or not
	 */
	isRemixGenerated: boolean;
	dynamicUiSubtype: string;
	/**
	 * Set to `true` by the backend when the generation/regeneration was blocked
	 * because the content did not meet safety / AUP guidelines. When this is
	 * `true` the completion is an expected (non-error) outcome and no media/MAUI
	 * content will be present on the payload.
	 */
	isSafetyViolation?: boolean;
	/**
	 * Human-readable error message returned by the backend (e.g. for a safety
	 * violation). Present on completions that carry an error but are not treated
	 * as a task failure.
	 */
	error?: string;
};

/**
 * Data payload for updating a Dynamic UI on a Confluence page.
 */
export type ConfluenceUpdateDynamicUiOnPageData = {
	/**
	 * The Maui ID of the dynamic UI to be updated.
	 */
	id: string;
	/**
	 * The previous Maui ID of the dynamic UI.
	 */
	previousMauiId: string;
	/**
	 * The local ID of the selection.
	 */
	selectionLocalId: string;
	/**
	 * The remix content type (e.g. 'chart', 'diagram') returned by the backend.
	 * Maps to `remixType` in experience attributes.
	 */
	contentType?: string;
	/**
	 * The MAUI id rendered in the completed ADF when it differs from the initial id.
	 */
	pageReadyMauiId?: string;
} & ConfluenceUpdatePageCommonData;

/**
 * Data payload for updating an image on a Confluence page.
 */
export type ConfluenceUpdateImageOnPageData = {
	/** Identifies the direct toolbar edit whose lifecycle is owned by the editor bridge. */
	imageEditAction?: 'removeBackground';
	/**
	 * The media file id of the image to be updated.
	 */
	mediaFileId: string;
	/**
	 * The previous media file id of the image.
	 */
	previousMediaFileId: string;
	/**
	 * A signed URL pointing to the new image binary in the MAUI personal collection..
	 */
	mediaUrl: string;
	/**
	 * The MAUI personal collection the new image lives in.
	 */
	mediaCollection: string;
	/**
	 * Generated alt text for the image (max 510 chars). A non-blank string sets the media node's alt.
	 * Missing, null or empty means 'no alt': nothing is set on insert, and any previous alt is cleared on replace.
	 */
	altText?: string | null;
	/**
	 * Existing dynamic UI source.
	 */
	dynamicUiSource: {
		/**
		 * The local ID of the selection.
		 */
		startLocalId: string;
	};
	/** Semantic Remix type (for example, `image` or `infographic`). */
	dynamicUiType?: string;
} & ConfluenceUpdatePageCommonData;

/**
 * Properties for the Confluence update page command.
 *
 * @extends CommandProperties
 */
export interface ConfluenceUpdatePageCommandBaseProperties extends CommandProperties {
	/** Runtime that authored the page update. */
	agentType?: AgentType;
	/**
	 * Whether the stream is complete.
	 */
	isStreamComplete: boolean;
	/**
	 * Trace id of the request
	 */
	traceId?: string;
	/**
	 * Request id for E2E event tracking and request correlation
	 */
	requestId?: string;
	/**
	 * Single instrumentation id of the request
	 * This is used to align events between rovo and the page command that may have
	 * actioned / dismissed the command
	 */
	singleInstrumentationId: string | undefined;
	/** Keeps Remix completion correlated when chat replaces the temporary message ID. */
	chatRemixInstrumentationId?: string;
	/**
	 * Agent identifier for the update.
	 */
	agentId: string | undefined;
	/**
	 * Human-readable agent display name for the telepointer label.
	 */
	agentName?: string;
	/**
	 * Atlassian identity account ID for the agent — used to generate the agent avatar via GeneratedAvatar.
	 */
	agentIdentityAccountId?: string | null;
	/**
	 * True when the update was driven by an inline edit — a free-gen
	 * Rovo prompt issued from inside the Editor (i.e. the originating
	 * `creationContextParams.experience === 'inline_edit'`). The editor receiver
	 * forwards this to the AI streaming orchestrator so the Post Stream Review
	 * "Review moment" auto-opens as the editor modal. Optional/undefined for
	 * non-inline update sources.
	 */
	isInlineEdit?: boolean;
	/**
	 * Opaque upstream creation experience for retention analytics. Preserve the
	 * supplied value; isInlineEdit/isChatEdit describe a separate dimension.
	 */
	creationExperience?: string;
	/**
	 * True when the update was driven from Rovo chat (`chat_edit`, `cwr`,
	 * `cwr_type`, or `cwr_edit`). The editor receiver forwards this so the
	 * Review moment can auto-open headless (controls in chat, no editor modal).
	 * Distinct from `isInlineEdit`.
	 */
	isChatEdit?: boolean;
	/**
	 * Marks an ADF update as a CWR Remix edit eligible for per-result acceptance analytics.
	 * Optional to preserve the wire contract for independently deployed senders and receivers.
	 */
	isCwrRemixEdit?: boolean;
	/** Identifies the CWR surface that initiated the Remix edit. */
	cwrRemixInvokedFrom?: CwrRemixInvokedFrom;
	/**
	 * External config reference (named_id) for the agent — used for OOTB agent avatar lookup.
	 */
	agentNamedId?: string | null;
	/**
	 * True for a Forge / third-party / remote agent (derived from the agent record's `creator_type`).
	 * Drives the telepointer's third-party treatment (neutral pill plus the agent name).
	 */
	isThirdParty?: boolean;
}

export type ConfluenceUpdatePageCommandProperties = ConfluenceUpdatePageCommandBaseProperties &
	(
		| {
				key: 'aifc-adf-update';
				data: ConfluenceUpdatePageUsingToolCallsData;
		  }
		| {
				key: 'dynamic-ui-create';
				data: ConfluenceUpdateDynamicUiOnPageData;
		  }
		| {
				key: 'image-create';
				data: ConfluenceUpdateImageOnPageData;
		  }
	);

/**
 * A command sent by a bridge API client to update a Confluence page.
 *
 * @summary {@link CommandProperties.senderId} is requesting {@link CommandProperties.receiverId} to create a Confluence page with the specified content.
 */
export const ConfluenceUpdatePageCommand: CommandConstructor<
	ConfluenceUpdatePageCommandProperties,
	SerializableValue
> = createCommand<ConfluenceUpdatePageCommandProperties>('confluence-page-update-command');
